import { PollSignInPromptHost } from './polls/PollSignInPromptHost';
import { lazy, Suspense } from 'react';
import { ScrollRestoration } from 'react-router-dom';
import { isNativePlatform } from '@lib/platform';
import { WebLogtoAdapterProvider, NativeLogtoAdapterProvider } from '@lib/logto';
import { AuthProvider } from '@providers';
import { BadgeProvider } from '@providers/BadgeProvider';
import { LocaleProvider } from '@providers/LocaleProvider';
import { InstallPromptProvider } from '@providers/InstallPromptProvider';
import { AnalyticsBootstrapper } from './AnalyticsBootstrapper';
import { AuthRouteOverlayReset } from './AuthRouteOverlayReset';
import { ReferralCodeSync } from './ReferralCodeSync';
import { AttributionCapture } from './AttributionCapture';
import { NativePushInitializer } from './NativePushInitializer';
import { NrefBootResolver } from './NrefBootResolver';
import { WebPushInitializer } from './WebPushInitializer';
import { InstallPromptInitializer } from './InstallPromptInitializer';
import { NativePostAuthHandler } from './NativePostAuthHandler';
import { RouteFallback } from './RouteFallback';
import { RootOutlet } from './PostSheet/RootOutlet';
import { scrollRestorationKey } from '../router/scrollRestorationKey';
import { SignedOutByOtherTabDrawer } from './SignedOutByOtherTabDrawer';
import { EventStreamProvider } from '@providers/EventStreamProvider';
import { StableRouterProvider } from '@providers/StableRouterProvider';
import { featureFlags } from '@config/featureFlags';

// Its own path, never the `@providers` barrel: 20+ suites blanket-mock that
// barrel, and a mocked barrel would silently unmount the continuation watcher.
// Lazy, so the watcher and the status request stay out of the entry chunk.
const MagicLinkContinuationWatcher = lazy(() =>
  import('@providers/MagicLinkContinuationProvider').then(module => ({
    default: module.MagicLinkContinuationWatcher,
  })),
);

// AIDEV-NOTE: Platform-conditional adapter -- uses @logto/capacitor on native (RFC 8252
// system browser auth) and @logto/react on web (SPA redirect auth). Both expose the same
// LogtoAdapterValue interface via useLogtoAdapter().
const LogtoAdapterProvider = isNativePlatform()
  ? NativeLogtoAdapterProvider
  : WebLogtoAdapterProvider;

// AIDEV-NOTE: Root layout wraps entire app with providers, ScrollRestoration, and global Suspense
// ScrollRestoration is React Router's native component - requires data router (createBrowserRouter)
export function RootLayout() {
  return (
    // StableRouterProvider is outermost so it covers every route: it holds the app's single router
    // subscription on behalf of the list leaves below, which navigate but must not re-render when
    // the URL moves (see stableRouterContext.ts).
    <StableRouterProvider>
      <LogtoAdapterProvider>
        <AuthProvider>
          {isNativePlatform() && <NativePostAuthHandler />}
          <EventStreamProvider>
            <LocaleProvider>
              {!isNativePlatform() && <SignedOutByOtherTabDrawer />}
              {/* Router-context bridge to the out-of-router overlay hosts in
                  index.tsx: nothing from the previous screen survives onto an
                  auth route. See the component for why the door hop needs it. */}
              <AuthRouteOverlayReset />
              <AnalyticsBootstrapper />
              {/* A leaf, lazily and only behind the flag: a flag-off build never
                  fetches it (F-1 of review 5330766393). Inside AuthProvider (it
                  reads the raw session flag) and inside StableRouterProvider (it
                  navigates and reads the URL without subscribing); it provides
                  no context and renders nothing, so it wraps nothing. */}
              {featureFlags.magicLinkContinuation && (
                <Suspense fallback={null}>
                  <MagicLinkContinuationWatcher />
                </Suspense>
              )}
              <ReferralCodeSync />
              <AttributionCapture />
              {isNativePlatform() && <NativePushInitializer />}
              <InstallPromptProvider>
                {!isNativePlatform() && <WebPushInitializer />}
                {!isNativePlatform() && <NrefBootResolver />}
                {!isNativePlatform() && <InstallPromptInitializer />}
                <ScrollRestoration getKey={scrollRestorationKey} />
                <BadgeProvider>
                  {/* Cream ground, no spinner, quiet until the wait is real.
                      See RouteFallback for why the old blue spinner had to go. */}
                  <Suspense fallback={<RouteFallback />}>
                    {/* The route table; with featureFlags.postSheet, able to draw the Post sheet
                        over a background (src/components/PostSheet/RootOutlet.tsx). */}
                    <RootOutlet />
                  </Suspense>
                  {/* B7: the sign-in drawer for an answer saved signed out (TECH-197). At the
                      root, as a tap in the Post sheet closes the sheet before it opens. */}
                  <PollSignInPromptHost />
                </BadgeProvider>
              </InstallPromptProvider>
            </LocaleProvider>
          </EventStreamProvider>
        </AuthProvider>
      </LogtoAdapterProvider>
    </StableRouterProvider>
  );
}
